import { useEffect, useLayoutEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Footer } from './Footer';
import { AnnouncementBanner } from '@/components/AnnouncementBanner';
import { useMe } from '@/hooks/useMe';
import { useAuthStore } from '@/stores/auth';

/**
 * 전체 레이아웃.
 *   - Landing (/ + 비로그인) : 사이드바 숨김 + 풀폭 (섹션 자체에 패딩)
 *   - /admin                  : 사이드바 숨김 + 컨테이너 풀폭 (관리자 콘솔 일관 UX)
 *   - 그 외 로그인 사용자     : 좌측 사이드바 + 컨테이너
 *
 * 새로고침 시 깜박임 방지:
 *   useMe 응답이 오기 전에 Landing 이 잠깐 렌더되면, 브라우저 scroll restoration
 *   이 이전 위치(예: CTA 섹션 하단)로 복원되어 어색하게 보임. 그래서 인증 확인이
 *   끝나기 전까지는 화면 전체 spinner 만 보여준다.
 */
export function AppLayout() {
  useMe();
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // 라우트 변경 시 스크롤 top 으로 강제 — Dashboard 진입 시 이전 위치 잔존 방지
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // 브라우저 자동 스크롤 복원 비활성 (새로고침 시 항상 top)
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // 인증 확인 중에는 화면 전체 spinner — Landing 깜박임 제거
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030712]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold/30 border-t-gold" />
      </div>
    );
  }

  const path = location.pathname;
  // /landing 명시 진입(로그인 사용자도) 또는 /+비로그인 → Landing 풀-블리드
  const isLanding = path === '/landing' || (path === '/' && !user);
  const isAdmin = path === '/admin' || path.startsWith('/admin/');
  const showSidebar = !!user && !isLanding && !isAdmin;
  const noContainer = isLanding; // Landing 만 풀-블리드

  return (
    <div className="min-h-screen flex flex-col bg-[#030712]">
      <Header onToggleSidebar={() => setSidebarOpen((o) => !o)} />
      {!isLanding && !isAdmin && <AnnouncementBanner />}

      <div className="flex flex-1">
        {showSidebar && <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />}

        <main className="flex-1 min-w-0 page-enter">
          <div className={noContainer ? '' : 'mx-auto w-full max-w-7xl px-4 sm:px-6 py-6 md:py-8'}>
            <Outlet />
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
